import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Animate } from "react-animate-mount";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import Stepper from "components/stepper/Stepper";
import PredictorOverview from "components/predictors/PredictorOverview";
import TextEditor from "components/textEditor/TextEditor";
import usePredictor from "hooks/predictors/usePredictor";
import usePredictorForm from "hooks/predictors/usePredictorForm";
import PredictorView from "components/predictors/PredictorView";

const ManagePredictor = ({ isEditable }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const { t } = useTranslation();
  const { id } = useParams();
  const { current, updatePredictor, createPredictor, deletePredictor } =
    usePredictor({ id });
  const { errors, FIELDS, values, setFieldValue, handleSubmit } =
    usePredictorForm({
      id,
      predictor: current || {},
      create: !id ? createPredictor : () => {},
      update: id ? updatePredictor : () => {},
    });
  useEffect(() => {
    setIsMounted(true);
  }, []);
  const steps = [
    {
      id: 1,
      title: "Step 1",
      desc: t("predictors.general"),
      content: (
        <PredictorOverview
          errors={{
            firstName: errors[FIELDS.FIRST_NAME],
            lastName: errors[FIELDS.LAST_NAME],
            description: errors[FIELDS.DESCRIPTION],
            image: errors[FIELDS.IMAGE],
            degree: errors[FIELDS.DEGREE],
            categories: errors[FIELDS.CATEGORIES],
          }}
          overview={{
            firstName: values[FIELDS.FIRST_NAME],
            lastName: values[FIELDS.LAST_NAME],
            description: values[FIELDS.DESCRIPTION],
            image: values[FIELDS.IMAGE],
            degree: values[FIELDS.DEGREE],
            categories: values[FIELDS.CATEGORIES],
          }}
          setFieldValue={setFieldValue}
          deletePredictor={deletePredictor}
          id={id}
        />
      ),
    },
    {
      id: 2,
      title: "Step 3",
      desc: t("predictors.content"),
      content: (
        <div className="pb-20">
          <TextEditor
            isEditable={isEditable}
            setValue={(value) => {
              setFieldValue(FIELDS.CONTENT, value);
            }}
            value={values[FIELDS.CONTENT]}
            height="100%"
            width="100%"
          />
        </div>
      ),
    },
    {
      id: 3,
      title: "Step 3",
      desc: t("predictors.review"),
      content: <PredictorView isEditable={isEditable} predictor={values} />,
    },
    {
      id: 4,
      title: "Step 3",
      desc: t("predictors.publish"),
      content: (
        <div className="flex items-center justify-center text-green-600 text-[32px]">
          Հրապարակված է
        </div>
      ),
    },
  ];

  return (
    <div className="mt-10 flex flex-col justify-center">
      <Animate show={isMounted}>
        <Stepper
          steps={steps}
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          isWithErrors={Object.keys(errors).length}
        />
      </Animate>
      <div className="flex w-full justify-between mt-10">
        {activeStep < steps.length - 1 && (
          <div
            onClick={() => activeStep > 0 && setActiveStep(activeStep - 1)}
            className={`p-2 bg-opacity-50 hover:bg-gray-400 text-gray-600 hover:text-white z-50 flex ${
              activeStep > 0 ? "opacity-100 cursor-pointer" : "opacity-0"
            }`}
          >
            <FaChevronLeft size={32} />
          </div>
        )}
        <div className="w-full">
          {steps.map((step, index) => {
            return (
              <Animate show={index === activeStep} key={index}>
                {step.content}
              </Animate>
            );
          })}
        </div>
        {activeStep <= 3 &&
          !Object.keys(errors).length &&
          values[FIELDS.FIRST_NAME] && (
            <div
              onClick={() => {
                if (activeStep === 2) {
                  setActiveStep(activeStep + 1);
                  handleSubmit();
                } else {
                  setActiveStep(activeStep + 1);
                }
              }}
              className={`p-2 bg-opacity-50 cursor-pointer flex justify-end hover:bg-gray-400 text-gray-600 hover:text-white ${
                steps.length !== activeStep ? "opacity-100" : "opacity-0"
              }`}
            >
              <FaChevronRight
                size={32}
                color={activeStep === 2 ? "#2E38FF" : ""}
              />
            </div>
          )}
      </div>
    </div>
  );
};

export default ManagePredictor;
